/**
 * @file DemoAskPanel.tsx
 * @brief Ask on the demo meeting: pick a question, get the answer with the
 *        lines it comes from. Fixed answers, no model call, no allowance used.
 *
 * Related: ./demoMeeting.ts (DEMO_ASK), ../company-meetings/components/AskMeetingsPanel.tsx
 *
 * @author DES
 * @date 20-10-2026
 */

import { useState } from "react";
import { Quote, Sparkles } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { CopyAnswer } from "@/features/company-meetings/components/AskMeetingsPanel";
import { DEMO_ASK, demoLine } from "./demoMeeting";

function clock(secs: number) {
  return `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
}

export function DemoAskPanel() {
  const [picked, setPicked] = useState<number | null>(null);
  const item = picked != null ? DEMO_ASK[picked] : null;

  return (
    <div className="px-5 pb-5 pt-2">
      <p className="mb-3 text-[13px] text-app-text-secondary">
        Demo: pick a question. In your own meetings you can ask anything.
      </p>
      <div className="flex flex-wrap gap-2">
        {DEMO_ASK.map((q, i) => (
          <button
            key={q.question}
            type="button"
            onClick={() => setPicked(i)}
            className={cn(
              "rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
              picked === i
                ? "bg-brand/10 text-brand"
                : "bg-black/[0.04] text-app-text hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]",
            )}
          >
            {q.question}
          </button>
        ))}
      </div>

      {item ? (
        <div className="mt-5" aria-live="polite">
          <div className="flex items-start gap-2">
            <p className="flex flex-1 items-start gap-2 text-[15px] leading-[23px] text-app-text">
              <Sparkles className="mt-1 h-4 w-4 shrink-0 text-brand" aria-hidden />
              {item.answer}
            </p>
            {/* As a real answer: copy it (21-10-2026). */}
            <CopyAnswer text={item.answer} />
          </div>
          <ul className="mt-3 space-y-2">
            {item.from.map((id) => {
              const line = demoLine(id);
              if (!line) return null;
              return (
                <li key={id} className="flex gap-2 rounded-xl bg-black/[0.03] p-3 dark:bg-white/[0.04]">
                  <Quote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-app-text-secondary" aria-hidden />
                  <span className="text-sm text-app-text-secondary">
                    <span className="font-semibold text-app-text">{line.speaker}</span>
                    <span className="tabular-nums"> · {clock(line.at)}</span>
                    <br />
                    {line.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
